import classNames from "classnames"
import { Link } from "gatsby"
import {
  FC,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  ArrowRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  Typography
} from "@eventbrite/marmalade"

// components
import { MicroCopy } from "../ContactForm/ContactForm"

// utils
import {
  navigateWithPrefix,
  returnUrlWithLangCodePrefixOnly,
  returnUrlWithPrefix
} from "../../utils/general_utils"
import { returnLoginUrl } from "../ProfileMenuBar/ProfileMenuBar"
import { openChat } from "../ChatWidget/LiveChatUtils"

// types
import { RootState } from "../../store/store"
import { SessionStorageKeys } from "../../types/window_session_storage.enum"
import { userProfileGuardCheck } from "../../store/slices/user_profile.slice"

// styles
import {
  articleBody,
  codeRedArticleBody,
  articleHeader,
  articleSection,
  articleSummary,
  bodyCopy,
  ctaSection,
  header,
  iconContainer,
  iconWrapper,
  lastArticle,
  lastSection,
  questionSection,
  relatedArticleLinks,
  codeRedRelatedArticleSection,
  relatedArticleSection,
  sectionHeader,
  firstSectionHeader,
  stillNeedHelpSection
} from "./CommonQuestions.module.scss"
import { renderEnhancedRichText } from "../../utils/contentful/enhanced-rich-text-renderer"

type ContentfulLandingPage = NonNullable<
  Queries.CommonIssuesAndQuestionsQuery["contentfulLandingPage"]
>

export type Props = {
  audience: ContentfulLandingPage["attending"]
  audienceQuestionsSectionMicroCopy: Queries.CommonIssuesAndQuestionsQuery["audienceQuestionMicroCopy"]
  messageLoginContactUsMicroCopy: Queries.CommonIssuesAndQuestionsQuery["messageLoginContactUsMicroCopy"]
  chatProvisioned: boolean
  withinChatBusinessHours: boolean
}

type MicroCopySet = {
  headerContactUs: MicroCopy
  messageContactUs: MicroCopy
  headerRelatedArticles: MicroCopy
}

const FALLBACK_MESSAGE_LOGIN_CONTACT_US_MICROCOPY: Queries.CommonIssuesAndQuestionsQuery["messageLoginContactUsMicroCopy"] =
  {
    microcopyValue: {
      raw: '{"data":{},"content":[{"data":{},"content":[{"data":{},"marks":[],"value":"Still have questions? Log in to contact support.","nodeType":"text"}],"nodeType":"paragraph"}],"nodeType":"document"}'
    }
  }

const AudienceQuestionsSection: FC<Props> = ({
  audience,
  audienceQuestionsSectionMicroCopy,
  messageLoginContactUsMicroCopy = FALLBACK_MESSAGE_LOGIN_CONTACT_US_MICROCOPY,
  chatProvisioned,
  withinChatBusinessHours
}) => {
  const initialOpenSection = useMemo(() => {
    if (typeof window !== "undefined" && window?.sessionStorage?.getItem) {
      const persistentSectionTitle = window?.sessionStorage.getItem(
        SessionStorageKeys.contactUsAccordionSectionTitle
      )

      if ((persistentSectionTitle?.length ?? 0) > 0) {
        return persistentSectionTitle
      } else {
        return ""
      }
    } else {
      return ""
    }
  }, [])

  const dispatch = useDispatch()

  const userProfile = userProfileGuardCheck(
    useSelector((state: RootState) => state.userProfile)
  )

  const [chevronDirection, setChevronDirection] = useState(initialOpenSection)
  const [openSection, setOpenSection] = useState(initialOpenSection)

  const handleChatCtaClick = useCallback<() => void>(() => {
    openChat(dispatch)
  }, [dispatch])

  const microCopy = useMemo(
    () => audienceQuestionsSectionMicroCopy,
    [audienceQuestionsSectionMicroCopy]
  )
  const microCopySet = useMemo<MicroCopySet | null>(() => {
    const objMap = microCopy?.microcopySetEntries?.map((entry) =>
      Object.values(entry as { [key: string]: MicroCopy })
    )
    if (!objMap) return null
    return Object.fromEntries(
      objMap as Iterable<[string, MicroCopy]>
    ) as MicroCopySet
  }, [microCopy])

  const constructCtaSection = useCallback((microCopy: string | undefined) => {
    if (!microCopy) {
      return null
    } else {
      // Get the string returned
      const richTxt = renderEnhancedRichText(microCopy)
      const value = Array.isArray(richTxt) ? richTxt[0].props.children[0] : ""
      const index = value.indexOf("?")
      const firstHalf = value.slice(0, index + 1)
      const secondHalf = value.slice(index + 1)

      return (
        <>
          <span className={questionSection}>{firstHalf}</span>
          <span className={ctaSection}>{secondHalf}</span>
        </>
      )
    }
  }, [])

  const handleLogInClick = useCallback((slug: string) => {
    if (typeof window !== "undefined") {
      if (window?.sessionStorage?.setItem) {
        window?.sessionStorage?.setItem(
          SessionStorageKeys?.contactFormSlug,
          slug
        )
      }
    }
  }, [])

  useEffect(() => {
    if (typeof window !== "undefined") {
      if (window?.sessionStorage?.setItem && typeof openSection === "string") {
        window?.sessionStorage?.setItem(
          SessionStorageKeys?.contactUsAccordionSectionTitle,
          openSection
        )
      }

      // on unmount of this accordion/navigation away from the page we want to clear the persistent data for the accordion (may need to look into other options here)
      return () => {
        window?.sessionStorage.removeItem(
          SessionStorageKeys.contactUsAccordionSectionTitle
        )
      }
    }
  }, [openSection])

  useEffect(() => {
    setOpenSection(chevronDirection)
  }, [chevronDirection])

  const clickHandler = useCallback(
    (title: string) => {
      if (chevronDirection === title) {
        setChevronDirection("")
      } else {
        setChevronDirection(title)
      }
    },
    [chevronDirection]
  )

  return (
    <div>
      {audience?.content?.map((question, i) => {
        if (question && "faqCategoryHeader" in question) {
          return (
            <div
              className={
                // different style applies for the last section
                i === (audience?.content?.length ?? 0) - 1
                  ? lastSection
                  : undefined
              }
              key={`${question?.faqCategoryHeader}--${i}`}
            >
              <h3
                className={classNames(sectionHeader, {
                  [`${firstSectionHeader}`]: i === 0
                })}
              >
                {question?.faqCategoryHeader}
              </h3>
              {question?.sections?.map((section, i) => {
                const showContactFormLink = () => {
                  if (!section?.isCodeRed) {
                    return false
                  }

                  if (chatProvisioned && section?.promoteChat) {
                    return false
                  }
                  if (
                    section.isCodeRed &&
                    section.isContactFormAvailableLoggedOut
                  ) {
                    return true
                  }

                  if (
                    audience?.sectionTitle
                      ?.toLowerCase()
                      ?.includes("organiz") &&
                    !userProfile?.isOrganizer
                  ) {
                    return false
                  }

                  if (
                    section.isContactFormAvailableLoggedOut === false &&
                    (!userProfile || !userProfile?.allData)
                  ) {
                    return false
                  }

                  return true
                }
                const showChatCTA = () => {
                  if (!chatProvisioned || showContactFormLink()) {
                    return false
                  }
                  if (
                    chatProvisioned &&
                    withinChatBusinessHours &&
                    section?.isCodeRed &&
                    section?.promoteChat
                  ) {
                    return true
                  }
                }
                const showLoginPrompt = () => {
                  if (userProfile?.isOrganizer === false) {
                    return false
                  }

                  if (
                    showContactFormLink() === false &&
                    showChatCTA() === false &&
                    section?.isCodeRed
                  ) {
                    return true
                  }

                  return false
                }

                if (section && "title" in section) {
                  return (
                    <details
                      className={classNames(articleSection, {
                        // different style applies for the last article
                        [`${lastArticle}`]:
                          i === (question?.sections?.length ?? 0) - 1
                      })}
                      key={`${section?.title}--${i}`}
                      open={openSection === section?.title}
                    >
                      <summary
                        onClick={() => clickHandler(section?.title ?? "")}
                        className={articleSummary}
                      >
                        <Typography className={articleHeader} as="h4">
                          {section?.title}
                        </Typography>
                        <div className={iconContainer}>
                          {chevronDirection === section?.title ? (
                            <ChevronUpIcon size="large" />
                          ) : (
                            <ChevronDownIcon size="large" />
                          )}
                        </div>
                      </summary>
                      <div
                        className={
                          section?.isCodeRed ? codeRedArticleBody : articleBody
                        }
                      >
                        <div className={bodyCopy}>
                          {renderEnhancedRichText(
                            section?.body?.raw ?? undefined
                          )}
                        </div>
                        {section?.contactSummaryRelatedArticles && (
                          <div
                            className={
                              section?.isCodeRed
                                ? codeRedRelatedArticleSection
                                : relatedArticleSection
                            }
                          >
                            <h3 className={header}>
                              {renderEnhancedRichText(
                                microCopySet?.headerRelatedArticles?.raw ??
                                  undefined
                              )}
                            </h3>
                            <ul>
                              {section?.contactSummaryRelatedArticles?.map(
                                (article) => {
                                  return (
                                    <li
                                      className={relatedArticleLinks}
                                      key={article?.articleId}
                                    >
                                      <Link
                                        to={
                                          returnUrlWithLangCodePrefixOnly(
                                            `/articles/${article?.articleId}/${article?.slug}`
                                          ) ?? ""
                                        }
                                      >
                                        {article?.helpArticleTitle}
                                        <span className={iconWrapper}>
                                          <ArrowRightIcon size="xsmall" />
                                        </span>
                                      </Link>
                                    </li>
                                  )
                                }
                              )}
                            </ul>
                          </div>
                        )}
                        {showContactFormLink() ? (
                          <div className={stillNeedHelpSection}>
                            {" "}
                            {section?.contactFormUrl ? (
                              <a href={section?.contactFormUrl}>
                                {constructCtaSection(
                                  section?.contactFormCtaCopy?.microcopyValue
                                    ?.raw ?? undefined
                                )}
                              </a>
                            ) : (
                              <h4
                                onClick={() => {
                                  navigateWithPrefix(
                                    `/contact-us/?form=${section?.contactForm?.contactFormUrl}`
                                  )
                                }}
                              >
                                {constructCtaSection(
                                  section?.contactFormCtaCopy?.microcopyValue
                                    ?.raw ?? undefined
                                )}
                              </h4>
                            )}
                          </div>
                        ) : showLoginPrompt() ? (
                          <div
                            className={stillNeedHelpSection}
                            onClick={() =>
                              handleLogInClick(
                                section?.contactForm?.contactFormUrl ?? ""
                              )
                            }
                          >
                            <LoginPrompt
                              messageLoginContactUsMicroCopy={
                                messageLoginContactUsMicroCopy
                              }
                              url={returnUrlWithPrefix(`/contact-us`) ?? ""}
                            />
                          </div>
                        ) : showChatCTA() ? (
                          <div className={stillNeedHelpSection}>
                            <h4
                              onClick={() => {
                                handleChatCtaClick()
                                handleLogInClick(
                                  section?.contactForm?.contactFormUrl ?? ""
                                )
                              }}
                            >
                              {constructCtaSection(
                                section?.contactFormCtaCopyProvisioned
                                  ?.microcopyValue?.raw ?? undefined
                              )}
                            </h4>
                          </div>
                        ) : null}
                      </div>
                    </details>
                  )
                }
              })}
            </div>
          )
        }
      })}
    </div>
  )
}

export const LoginPrompt: FC<{
  messageLoginContactUsMicroCopy: Queries.CommonIssuesAndQuestionsQuery["messageLoginContactUsMicroCopy"]
  url: string
}> = ({ messageLoginContactUsMicroCopy, url }) => {
  // When microcopyValue contains a simple paragraph, the renderer produces a
  // ReactElement array whose first element's children[0] is the plain string.
  const rendered = renderEnhancedRichText(
    messageLoginContactUsMicroCopy?.microcopyValue?.raw ?? undefined
  ) as ReactElement[] | null
  const rawStringValue: string | undefined =
    rendered?.[0]?.props?.children?.[0]

  if (rawStringValue) {
    const [question, link] = rawStringValue.split("? ")
    const finalUrl = returnLoginUrl(url)

    return (
      <>
        <span className={questionSection}>{question}?</span>{" "}
        <a href={finalUrl}>
          <span className={ctaSection}>{link}</span>
        </a>
      </>
    )
  }

  return null
}

export default AudienceQuestionsSection
